import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { saveQuizAttempt } from '../../services/quizAttempts';
import { updateUserXP } from '../../services/xpService';
import quizData from '../../quizzes/quizzes.json';
import './QuizSession.css';

const QuizSession = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const auth = getAuth();
    const { subject, grade, topic, subtopic } = location.state || {};
    
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [userTextAnswer, setUserTextAnswer] = useState('');
    const [showFeedback, setShowFeedback] = useState(false);
    const [score, setScore] = useState(0);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [incorrectAnswers, setIncorrectAnswers] = useState(0);
    const [timeLeft, setTimeLeft] = useState(150); // 2:30 minutes per question
    const [isTimerRunning, setIsTimerRunning] = useState(true);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCorrect, setIsCorrect] = useState(false);
    const [isExplanationCollapsed, setIsExplanationCollapsed] = useState(false);

    useEffect(() => {
        // Find the appropriate quiz based on subject, grade, topic, and subtopic
        if (subject && grade && topic && subtopic) {
            // Format topic name to match quizzes.json format (e.g., "Linear Equations")
            const formattedTopic = topic
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

            // Find the matching quiz key
            const quizKey = Object.keys(quizData).find(key => {
                return quizData[key].subject === subject && 
                       quizData[key].grade === parseInt(grade) &&
                       quizData[key].topic.toLowerCase().includes(topic.toLowerCase()) &&
                       quizData[key].subtopic.toLowerCase().includes(subtopic.toLowerCase());
            });

            if (quizKey && quizData[quizKey].questions) {
                setQuestions(quizData[quizKey].questions);
                setLoading(false);
            } else {
                // If no exact match, try to find the closest match
                const subjectQuizzes = Object.keys(quizData).filter(key => 
                    quizData[key].subject === subject &&
                    quizData[key].grade === parseInt(grade)
                );
                
                if (subjectQuizzes.length > 0) {
                    // Just use the first quiz for this subject and grade as a fallback
                    setQuestions(quizData[subjectQuizzes[0]].questions);
                    setLoading(false);
                } else {
                    // No quizzes found for this subject and grade
                    console.error('No questions found for the selected criteria');
                    setLoading(false);
                }
            }
        }
    }, [subject, grade, topic, subtopic]);

    useEffect(() => {
        let timer;
        if (isTimerRunning && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0) {
            setIsTimerRunning(false);
            handleAnswer(null);
        }
        return () => clearInterval(timer);
    }, [isTimerRunning, timeLeft]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleAnswer = (index) => {
        if (showFeedback) return;
        setIsTimerRunning(false);
        setShowFeedback(true);

        const currentQ = questions[currentQuestion];
        let isAnswerCorrect = false;

        if (currentQ.type === 'multiple_choice') {
            setSelectedAnswer(index);
            isAnswerCorrect = index === currentQ.correct;
        } else if (currentQ.type === 'fill_in') {
            const userAnswer = userTextAnswer.trim().toLowerCase();
            const correctAnswer = currentQ.answer.toLowerCase();
            isAnswerCorrect = userAnswer === correctAnswer;
        }

        if (isAnswerCorrect) {
            setScore(score + 1);
            setCorrectAnswers(prev => prev + 1);
            setIsCorrect(true);
        } else {
            setIncorrectAnswers(prev => prev + 1);
            setIsCorrect(false);
        }
    };

    const handleNext = async () => {
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
            setSelectedAnswer(null);
            setUserTextAnswer('');
            setShowFeedback(false);
            setTimeLeft(150);
            setIsTimerRunning(true);
            setIsCorrect(false);
        } else {
            // Quiz completed
            try {
                const timeSpent = 150 - timeLeft;
                const finalScore = Math.round((score / questions.length) * 100);
                
                // Save quiz attempt
                const attemptData = {
                    userId: auth.currentUser.uid,
                    subject,
                    grade: parseInt(grade),
                    topic,
                    subtopic,
                    score: finalScore,
                    totalQuestions: questions.length,
                    timeSpent
                };
                
                await saveQuizAttempt(attemptData);

                // Update XP
                const quizResults = {
                    correctAnswers,
                    incorrectAnswers,
                    completed: true
                };

                const xpEarned = await updateUserXP(auth.currentUser.uid, quizResults);

                // Navigate to results with XP information
                navigate('/quiz/results', { 
                    state: { 
                        score: finalScore, 
                        totalQuestions: questions.length,
                        subject,
                        grade,
                        topic,
                        subtopic,
                        xpEarned
                    }
                });
            } catch (error) {
                console.error('Error saving quiz attempt:', error);
                // Navigate to results even if saving fails
                navigate('/quiz/results', { 
                    state: { 
                        score: Math.round((score / questions.length) * 100), 
                        totalQuestions: questions.length,
                        subject,
                        grade,
                        topic,
                        subtopic
                    }
                });
            }
        }
    };

    const getOptionClass = (index) => {
        if (!showFeedback) return `quiz-option ${selectedAnswer === index ? 'selected' : ''}`;
        if (index === questions[currentQuestion].correct) return 'quiz-option correct';
        if (index === selectedAnswer && index !== questions[currentQuestion].correct) return 'quiz-option incorrect';
        return 'quiz-option';
    };

    if (loading) {
        return <div className="quiz-loading">Loading quiz questions...</div>;
    }

    if (questions.length === 0) {
        return <div className="quiz-error">No questions found for this quiz. Please try another topic.</div>;
    }

    if (!subject || !grade || !topic || !subtopic) {
        return <div className="quiz-error">Invalid quiz parameters. Please return to quiz selection.</div>;
    }

    return (
        <div className="quiz-session">
            <div className="quiz-header">
                <div className="quiz-info">
                    <h1>{subject.charAt(0).toUpperCase() + subject.slice(1)} Quiz</h1>
                    <p>Grade {grade} - {topic.charAt(0).toUpperCase() + topic.slice(1)} - {subtopic.charAt(0).toUpperCase() + subtopic.slice(1)}</p>
                </div>
                <div className="quiz-progress">
                    <div className="progress-bar">
                        <div 
                            className="progress" 
                            style={{ 
                                width: `${((currentQuestion + 1) / questions.length) * 100}%` 
                            }}
                        ></div>
                    </div>
                    <span>Question {currentQuestion + 1} of {questions.length}</span>
                </div>
            </div>

            <div className="quiz-content">
                <div className={`quiz-timer ${timeLeft <= 30 ? 'warning' : ''}`}>
                    <span className="material-icons">timer</span>
                    <span>{formatTime(timeLeft)}</span>
                </div>

                <div className="quiz-question">
                    <h2>{questions[currentQuestion].text}</h2>
                    {questions[currentQuestion].type === 'multiple_choice' ? (
                        <div className="quiz-options">
                            {questions[currentQuestion].options.map((option, index) => (
                                <button
                                    key={index}
                                    className={getOptionClass(index)}
                                    onClick={() => handleAnswer(index)}
                                    disabled={showFeedback}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="fill-in-answer">
                            <input
                                type="text"
                                value={userTextAnswer}
                                onChange={(e) => setUserTextAnswer(e.target.value)}
                                placeholder="Enter your answer"
                                disabled={showFeedback}
                            />
                            <button 
                                className="submit-answer"
                                onClick={() => handleAnswer(null)}
                                disabled={showFeedback || !userTextAnswer.trim()}
                            >
                                Submit
                            </button>
                        </div>
                    )}
                </div>

                {showFeedback && (
                    <div className="quiz-feedback">
                        <div>
                            {isCorrect ? (
                                <>
                                    <span className="material-icons success">check_circle</span>
                                    <span>Correct! Well done!</span>
                                </>
                            ) : (
                                <>
                                    <span className="material-icons error">error</span>
                                    <span>Not quite right. The correct answer is: {
                                        questions[currentQuestion].type === 'multiple_choice' 
                                            ? questions[currentQuestion].options[questions[currentQuestion].correct]
                                            : questions[currentQuestion].answer
                                    }</span>
                                </>
                            )}
                        </div>
                        <div className={`explanation ${isExplanationCollapsed ? 'collapsed' : ''}`}>
                            <h3 onClick={() => setIsExplanationCollapsed(!isExplanationCollapsed)}>
                                Explanation
                            </h3>
                            <p>{questions[currentQuestion].explanation}</p>
                        </div>
                        <button className="next-button" onClick={handleNext}>
                            {currentQuestion < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuizSession; 